<template>
  <div class="annual-summary">
    <div class="page-header">
      <h2>年汇总</h2>
    </div>

    <!-- 查询条件 -->
    <a-card class="filter-card">
      <a-form
        :model="searchForm"
        layout="inline"
        @finish="handleSearch"
        class="search-form"
      >
        <a-form-item label="统计日期" name="yearRange">
          <div style="display: flex; align-items: center; gap: 8px;">
            <a-date-picker
              v-model:value="searchForm.startYear"
              picker="year"
              format="YYYY"
              placeholder="起始年"
              style="width: 110px"
              :disabled-date="disabledYear"
            />
            <span>~</span>
            <a-date-picker
              v-model:value="searchForm.endYear"
              picker="year"
              format="YYYY"
              placeholder="结束年"
              style="width: 110px"
              :disabled-date="disabledYear"
            />
          </div>
        </a-form-item>

        <a-form-item label="财务分类" name="categoryId">
          <a-select
            v-model:value="searchForm.categoryId"
            placeholder="请选择分类"
            style="width: 200px"
            allowClear
            @focus="loadCategories"
          >
            <a-select-option
              v-for="category in categories"
              :key="category.id"
              :value="category.id"
            >
              {{ category.name }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item>
          <a-button type="primary" html-type="submit" :loading="loading">
            <template #icon>
              <SearchOutlined />
            </template>
            查询
          </a-button>
          <a-button style="margin-left: 8px" @click="handleReset">
            重置
          </a-button>
          <a-button style="margin-left: 8px" @click="handleExport" :loading="exportLoading">
            <template #icon>
              <DownloadOutlined />
            </template>
            导出Excel
          </a-button>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 总汇总信息卡片 -->
    <a-card class="summary-card" :loading="loading" v-if="totalSummary">
      <a-row :gutter="32">
        <a-col :span="4">
          <a-statistic
            title="总记录数"
            :value="totalSummary.totalCount || 0"
            :value-style="{ fontSize: '20px', fontWeight: 'bold' }"
          />
        </a-col>
        <a-col :span="4">
          <a-statistic
            title="收入笔数"
            :value="totalSummary.incomeCount || 0"
            :value-style="{ color: '#52c41a', fontSize: '20px', fontWeight: 'bold' }"
          />
        </a-col>
        <a-col :span="4">
          <a-statistic
            title="支出笔数"
            :value="totalSummary.expenseCount || 0"
            :value-style="{ color: '#ff4d4f', fontSize: '20px', fontWeight: 'bold' }"
          />
        </a-col>
        <a-col :span="4">
          <a-statistic
            title="收入总额"
            :value="Number(totalSummary.incomeAmount || 0).toFixed(2)"
            :value-style="{ color: '#52c41a', fontSize: '20px', fontWeight: 'bold' }"
            prefix="¥"
          />
        </a-col>
        <a-col :span="4">
          <a-statistic
            title="支出总额"
            :value="Number(totalSummary.expenseAmount || 0).toFixed(2)"
            :value-style="{ color: '#ff4d4f', fontSize: '20px', fontWeight: 'bold' }"
            prefix="¥"
          />
        </a-col>
        <a-col :span="4">
          <a-statistic
            title="净值"
            :value="Number(totalSummary.netAmount || 0).toFixed(2)"
            :value-style="{
              color: (totalSummary.netAmount || 0) >= 0 ? '#52c41a' : '#ff4d4f',
              fontSize: '20px',
              fontWeight: 'bold'
            }"
            prefix="¥"
          />
        </a-col>
      </a-row>
    </a-card>

    <!-- 年汇总表格 -->
    <a-card>
      <a-table
        :columns="columns"
        :data-source="summaryList"
        :loading="loading"
        row-key="key"
        size="middle"
        bordered
        :pagination="false"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'incomeAmount'">
            <span style="color: #52c41a; font-weight: bold;">
              +{{ Number(record.incomeAmount || 0).toFixed(2) }}
            </span>
          </template>

          <template v-else-if="column.key === 'expenseAmount'">
            <span style="color: #ff4d4f; font-weight: bold;">
              -{{ Number(record.expenseAmount || 0).toFixed(2) }}
            </span>
          </template>

          <template v-else-if="column.key === 'netAmount'">
            <span :style="{
              color: (record.netAmount || 0) >= 0 ? '#52c41a' : '#ff4d4f',
              fontWeight: 'bold'
            }">
              {{ (record.netAmount || 0) >= 0 ? '+' : '' }}{{ Number(record.netAmount || 0).toFixed(2) }}
            </span>
          </template>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<script>
import { ref, shallowRef, reactive, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons-vue'
import { financialApi } from '@/api/financial'
import { financialCategoryApi } from '@/api/financialCategory'
import dayjs from 'dayjs'

export default {
  name: 'AnnualSummary',
  components: {
    SearchOutlined,
    DownloadOutlined
  },
  setup() {
    const loading = ref(false)
    const exportLoading = ref(false)
    const summaryList = shallowRef([])  // 纯展示大列表：shallowRef 免深度响应式
    const totalSummary = ref(null)
    const categories = ref([])

    const searchForm = reactive({
      startYear: dayjs().subtract(4, 'year'),
      endYear: dayjs(),
      categoryId: undefined
    })

    const columns = [
      {
        title: '年份',
        dataIndex: 'keyName',
        width: 120,
        align: 'center'
      },
      {
        title: '收入笔数',
        dataIndex: 'incomeCount',
        width: 100,
        align: 'center'
      },
      {
        title: '支出笔数',
        dataIndex: 'expenseCount',
        width: 100,
        align: 'center'
      },
      {
        title: '收入金额',
        key: 'incomeAmount',
        width: 120,
        align: 'right'
      },
      {
        title: '支出金额',
        key: 'expenseAmount',
        width: 120,
        align: 'right'
      },
      {
        title: '净值',
        key: 'netAmount',
        width: 120,
        align: 'right'
      }
    ]

    const loadCategories = async () => {
      if (categories.value.length > 0) return

      try {
        const result = await financialCategoryApi.getTree()
        const flatCategories = []

        const flattenCategories = (cats, prefix = '') => {
          if (!cats || !Array.isArray(cats)) return

          cats.forEach(cat => {
            if (cat.status === 1 || cat.status === '1') {
              flatCategories.push({
                id: cat.id,
                name: prefix + cat.name
              })
              if (cat.children && cat.children.length > 0) {
                flattenCategories(cat.children, prefix + cat.name + ' / ')
              }
            }
          })
        }

        flattenCategories(result.data)
        categories.value = flatCategories
      } catch (error) {
        console.error('加载财务分类失败:', error)
      }
    }

    const loadSummary = async () => {
      loading.value = true
      try {
        const params = {
          summaryType: 'yearly'
        }

        if (searchForm.startYear) {
          params.startDate = searchForm.startYear.format('YYYY') + '0101'
        }
        if (searchForm.endYear) {
          params.endDate = searchForm.endYear.format('YYYY') + '1231'
        }

        if (searchForm.categoryId) {
          params.categoryId = searchForm.categoryId
        }

        const result = await financialApi.summary(params)
        summaryList.value = result.data.summaryList || []
        totalSummary.value = result.data.totalSummary || null
      } catch (error) {
        console.error('加载年汇总失败:', error)
        message.error('加载年汇总失败')
      } finally {
        loading.value = false
      }
    }

    const handleSearch = () => {
      loadSummary()
    }

    const handleReset = () => {
      searchForm.startYear = dayjs().subtract(4, 'year')
      searchForm.endYear = dayjs()
      searchForm.categoryId = undefined
      loadSummary()
    }

    const handleExport = async () => {
      exportLoading.value = true
      try {
        const params = {
          summaryType: 'yearly'
        }

        if (searchForm.startYear) {
          params.startDate = searchForm.startYear.format('YYYY') + '0101'
        }
        if (searchForm.endYear) {
          params.endDate = searchForm.endYear.format('YYYY') + '1231'
        }

        if (searchForm.categoryId) {
          params.categoryId = searchForm.categoryId
        }

        const response = await financialApi.exportSummary(params)

        const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `财务汇总_按年_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`
        a.click()
        window.URL.revokeObjectURL(url)

        message.success('导出成功')
      } catch (error) {
        console.error('导出失败:', error)
        message.error('导出失败')
      } finally {
        exportLoading.value = false
      }
    }

    const disabledYear = (current) => {
      return current && current.isAfter(dayjs().endOf('year'))
    }

    onMounted(async () => {
      await loadCategories()
      loadSummary()
    })

    return {
      loading,
      exportLoading,
      summaryList,
      totalSummary,
      categories,
      searchForm,
      columns,
      handleSearch,
      handleReset,
      handleExport,
      loadCategories,
      disabledYear
    }
  }
}
</script>

<style scoped>
.annual-summary {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.filter-card {
  margin-bottom: 16px;
}

.summary-card {
  margin-bottom: 16px;
}

.search-form .ant-form-item {
  margin-bottom: 0;
}
</style>